import { HttpErrorResponse } from '@angular/common/http';
import { AccountApiErrorItem } from '@limehome/account-api-client';
import { ACCOUNT_API_ERRORS } from '../../../constants/account-api-errors';
import { TranslationSlab } from '../translation-slab/translation-slab';
import { CookieAppService } from '@shared/common/services/cookie-app/cookie-app.service';
import { TranslateService } from '@ngx-translate/core';
import { Inject, PLATFORM_ID } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { DEFAULT_PASSWORD_STRENGTH } from '../../../constants/password.constant';
import { LocationService } from '../../services/location/location.service';
import { convertObjectToCamelCase } from '../../../utils/object/object';
import format from 'date-fns/format';
import parse from 'date-fns/parse';

export interface ProfileParams {
  phoneNumber: string;
  dateOfBirth: string;
  currentLang?: string;
  isCheckoutRegistration?: boolean;
}

export class ProfileHelper extends TranslationSlab {
  accountApiErrors = ACCOUNT_API_ERRORS;
  signUpPasswordScore = DEFAULT_PASSWORD_STRENGTH;

  constructor(
    public translateService: TranslateService,
    public cookieAppService: CookieAppService,
    public locationService: LocationService,
    @Inject(PLATFORM_ID) public platformId: Object
  ) {
    super(translateService, cookieAppService, locationService, platformId);
  }

  getErrorItem(error): AccountApiErrorItem | null {
    let errorItem = null;

    if (error instanceof HttpErrorResponse) {
      const body = convertObjectToCamelCase(error.error);

      if (body.errors && body.errors.length && body.errors[0].errorCode) {
        errorItem = body.errors[0];
      }
    }

    return errorItem;
  }

  getErrorTranslationKey(error): string {
    let errorText: string;
    let errorCode = null;

    const errorItem = this.getErrorItem(error);
    if (errorItem) {
      errorCode = errorItem.errorCode;
    }

    if (!errorCode) {
      errorCode = 'generic.unknown';
    }

    const errorCodeParts = errorCode.split('.');
    const errorCodeGroup = errorCodeParts[0];

    if (this.accountApiErrors[errorCodeGroup] && this.accountApiErrors[errorCodeGroup][errorCodeParts[1]]) {
      errorText = this.accountApiErrors[errorCodeGroup][errorCodeParts[1]];
    } else if (this.accountApiErrors[errorCodeGroup]) {
      errorText = this.accountApiErrors[errorCodeGroup].unknown;
    }

    if (!errorText) {
      errorText = this.accountApiErrors.generic.unknown;
    }

    return errorText;
  }

  validateStrength(controlName: string, formGroup: UntypedFormGroup) {
    // Load zxcvbn only when the modal is visible: please don't ask about its name or how to pronounce it :p
    import('zxcvbn').then((zxcvbn) => {
      const control = formGroup.controls[controlName];
      const controlValue = control.value || '';
      // checking if default exists and can be invoked as a function, falling back to default strength
      // this would happen if a bot was used to scrape/crawl the data from the page, but it wouldn't be an issue in modern browsers
      const score = zxcvbn?.default?.(controlValue).score ?? DEFAULT_PASSWORD_STRENGTH;

      this.signUpPasswordScore = score;

      if (control.errors && !control.errors.weak) {
        // return if another validator has already found an error on the control
        return null;
      }

      // set error on control if validation fails
      if (score < 2) {
        control.setErrors({ weak: true });
      } else {
        control.setErrors(null);
      }
    });
  }

  resetSignUpPasswordScore() {
    this.signUpPasswordScore = DEFAULT_PASSWORD_STRENGTH;
  }

  formatFormValues(values, { phoneNumber, dateOfBirth, currentLang, isCheckoutRegistration }: ProfileParams) {
    const formattedData = {
      firstName: values.firstName,
      lastName: values.lastName,
      dateOfBirth: dateOfBirth ? format(parse(dateOfBirth, 'dd/MM/yyyy', new Date()), 'yyyy/M/d') : null,
      gender: values.gender,
      phoneNumber: phoneNumber ? phoneNumber : null,
      languageCode: currentLang ? currentLang : values.languageCode,
      nationalityCode: values.nationalityCode,
      address: {
        streetAddress: isCheckoutRegistration ? values.address.addressLine1 : values.streetAddress,
        city: isCheckoutRegistration ? values.address.city : values.city,
        postalCode: isCheckoutRegistration ? values.address.postalCode : values.postalCode,
        countryCode: isCheckoutRegistration ? values.address.countryCode : values.countryCode
      }
    };
    return formattedData;
  }

  /**
   * It takes a form value as input and returns a formatted user profile object.
   * @param values the form values
   * @param currentLang the active language code
   */
  formatFormValuesCheckout(values, currentLang?: string) {
    return {
      firstName: values.firstName,
      lastName: values.lastName,
      dateOfBirth: values.dateOfBirth ? format(parse(values.dateOfBirth, 'dd/MM/yyyy', new Date()), 'yyyy/M/d') : null,
      gender: values.gender,
      phoneNumber: values.phone ?? null,
      languageCode: currentLang ? currentLang : values.languageCode,
      nationalityCode: values.nationalityCode,
      address: {
        streetAddress: values.streetAddress,
        city: values.city,
        postalCode: values.postalCode,
        countryCode: values.countryCode
      }
    };
  }
}
